// File: frontend/src/services/superAdmin/geoService.js

import { apiService } from '../api';
import { API_ENDPOINTS } from '../../config/constants';

export const geoService = {
  // Get geography data
  getGeographies: async (options = {}) => {
    console.group('Getting Geographies');
    console.log('Options:', options);
    
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.GEO.GET, {
        params: options
      });
      
      console.log('Geographies retrieved successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error getting geographies:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  getGeographyById: async (geoId, includeChildren = false, includeBoundary = false) => {
    console.group('Getting Geography by ID');
    console.log('Geography ID:', geoId);
    
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.GEO.GET, {
        params: {
          id: geoId,
          include_children: includeChildren,
          include_boundary: includeBoundary
        }
      });
      
      console.log('Geography retrieved successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error getting geography by ID:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  getGeographiesByType: async (geoType, options = {}) => {
    console.group('Getting Geographies by Type');
    console.log('Geography Type:', geoType);
    console.log('Options:', options);
    
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.GEO.GET, {
        params: {
          type: geoType,
          ...options
        }
      });
      
      console.log('Geographies by type retrieved successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error getting geographies by type:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  getGeographiesByParent: async (parentGeoId, options = {}) => {
    console.group('Getting Geographies by Parent');
    console.log('Parent Geography ID:', parentGeoId);
    console.log('Options:', options);
    
    try {
      const response = await apiService.get(API_ENDPOINTS.MASTER.GEO.GET, {
        params: {
          parent_id: parentGeoId,
          ...options
        }
      });
      
      console.log('Geographies by parent retrieved successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error getting geographies by parent:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  // Create a new geography
  createGeography: async (geographyData) => {
    console.group('Creating Geography');
    console.log('Geography Data:', geographyData);
    
    try {
      const response = await apiService.post(API_ENDPOINTS.MASTER.GEO.CREATE, geographyData);
      
      console.log('Geography created successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error creating geography:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  // Update an existing geography
  updateGeography: async (geographyData) => {
    console.group('Updating Geography');
    console.log('Geography Data:', geographyData);
    
    try {
      const response = await apiService.put(API_ENDPOINTS.MASTER.GEO.UPDATE, geographyData);
      
      console.log('Geography updated successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error updating geography:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  // Delete a geography
  deleteGeography: async (geoId) => {
    console.group('Deleting Geography');
    console.log('Geography ID:', geoId);
    
    try {
      const response = await apiService.delete(API_ENDPOINTS.MASTER.GEO.DELETE, {
        data: { geo_id: geoId }
      });
      
      console.log('Geography deleted successfully:', response);
      console.groupEnd();
      return response.data || response;
    } catch (error) {
      console.error('Error deleting geography:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  // Helper functions
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  },
  
  // Parse and validate GeoJSON
  validateGeoJSON: (geoJsonString) => {
    try {
      // Try to parse if it's a string
      const geoJson = typeof geoJsonString === 'string' 
        ? JSON.parse(geoJsonString) 
        : geoJsonString;
      
      // Basic validation
      if (!geoJson || typeof geoJson !== 'object') {
        return { valid: false, error: 'Invalid GeoJSON format' };
      }
      
      if (!geoJson.type) {
        return { valid: false, error: 'Missing "type" property' };
      }
      
      if (!geoJson.features && !geoJson.geometry) {
        return { valid: false, error: 'Missing "features" or "geometry" property' };
      }
      
      return { valid: true, geoJson };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  },

  // Batch create geographies
  batchCreateGeographies: async (geographiesData) => {
    console.group('Batch Creating Geographies');
    console.log('Geographies Data:', geographiesData);
    
    try {
      // Process in batches to avoid overwhelming the API
      const batchSize = 10;
      const results = [];
      
      for (let i = 0; i < geographiesData.length; i += batchSize) {
        const batch = geographiesData.slice(i, i + batchSize);
        
        // Process each item in the batch
        const batchResults = await Promise.allSettled(
          batch.map(geoData => 
            apiService.post(API_ENDPOINTS.MASTER.GEO.CREATE, geoData)
          )
        );
        
        // Map results
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            results.push({
              status: 'success',
              data: result.value.data,
              geoId: batch[index].geo_id
            });
          } else {
            results.push({
              status: 'error',
              error: result.reason,
              geoId: batch[index].geo_id
            });
          }
        });
      }
      
      console.log('Batch creation results:', results);
      console.groupEnd();
      return results;
    } catch (error) {
      console.error('Error in batch creating geographies:', error);
      console.groupEnd();
      throw error;
    }
  },
  
  // Process GeoJSON file
  processGeoJsonFile: async (file, options = {}) => {
    console.group('Processing GeoJSON File');
    console.log('File:', file);
    console.log('Options:', options);
    
    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
      
      // Parse GeoJSON
      const geoJson = JSON.parse(fileContent);
      
      // Validate GeoJSON
      const validationResult = geoService.validateGeoJSON(geoJson);
      if (!validationResult.valid) {
        throw new Error(validationResult.error);
      }
      
      // Extract features
      let features = [];
      if (geoJson.type === 'FeatureCollection') {
        features = geoJson.features;
      } else if (geoJson.type === 'Feature') {
        features = [geoJson];
      } else {
        throw new Error(`Unsupported GeoJSON type: ${geoJson.type}`);
      }
      
      // Process features
      const geographiesData = features.map(feature => {
        const properties = feature.properties || {};
        
        // Generate ID and code
        let geoId = properties.id || properties.ID || properties.geo_id || properties.GEO_ID;
        let geoCode = properties.code || properties.CODE || properties.geo_code || properties.GEO_CODE;
        let geoName = properties.name || properties.NAME || properties.geo_name || properties.GEO_NAME;
        
        // If no ID or code, generate from filename
        if (!geoId) {
          const baseName = file.name.split('.')[0];
          geoId = `${options.idPrefix || ''}${baseName}`.toLowerCase();
        }
        
        if (!geoCode) {
          geoCode = `${options.codePrefix || ''}${geoId}`.toUpperCase();
        }
        
        if (!geoName) {
          geoName = geoId.replace(/_/g, ' ').replace(/-/g, ' ');
          // Capitalize first letter of each word
          geoName = geoName.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
        
        // Create geography data
        return {
          geo_id: geoId,
          geo_type: options.geoType || 'custom',
          geo_name: geoName,
          geo_code: geoCode,
          parent_geo_id: options.parentGeoId || 'NONE',
          properties: {
            ...properties,
            source_file: file.name
          },
          geometry_boundary: feature
        };
      });
      
      console.log('Processed geographies data:', geographiesData);
      console.groupEnd();
      
      return {
        status: 'success',
        geographiesData,
        count: geographiesData.length
      };
    } catch (error) {
      console.error('Error processing GeoJSON file:', error);
      console.groupEnd();
      
      return {
        status: 'error',
        error: error.message,
        count: 0
      };
    }
  }
};

export default geoService;